import React, {useState} from "react";
import {Link, useParams} from "react-router-dom";
import {
  IconArrowRight,
  IconBook2,
  IconBuilding,
  IconBuildingBank,
  IconCalculator,
  IconCalendar,
  IconChartBar,
  IconCheck,
  IconCircleCheck,
  IconCreditCard,
  IconFileInvoice,
  IconFileText,
  IconHome,
  IconLock,
  IconMail,
  IconMapPin,
  IconPhone,
  IconPlus,
  IconShieldCheck,
  IconStarFilled,
  IconTrendingUp,
  IconUser,
  IconUsers,
  type Icon,
} from "@tabler/icons-react";

import classes from "./Services.module.scss";
import {HeroPhoneAnimation} from "@fig/client/routes/Home/HeroPhoneAnimation";
import {
  SERVICES_BRAND_NAME,
  SERVICES_DEFAULT_OFFICE_SLUG,
  SERVICES_PHONE_DISPLAY,
  SERVICES_PHONE_TEL,
  getServicesIndustryBySlug,
  getServicesOfficeBySlug,
  servicesIndustryPages,
  servicesLocationOffices,
  type ServicesCard,
  type ServicesIconKey,
  type ServicesOffice,
  type ServicesPackage,
} from "@fig/libs/shared/services/services-content";

const LOGO_SRC = "/assets/logo/fig-light.png";
const HERO_IMAGE_SRC = "/assets/services/locations/default/hero-advisor-client.png";
const ADVISOR_WOMAN_SRC = "/assets/services/locations/default/advisor-woman.png";
const ACCOUNTANT_SRC = "/assets/services/locations/default/accountant.png";
const BOOKKEEPER_SRC = "/assets/services/locations/default/bookkeeper.png";
const GOOGLE_IMAGE_SRC = "/assets/services/google.png";
const LOCATIONS_PATH = "/locations";
const INDUSTRIES_PATH = "/industries";
const SERVICE_SIGNUP_PATH = "/service-signup";
const PRICING_SECTION_ID = "pricing";

type SubmissionState = "idle" | "submitting" | "success" | "error";

type ServicesLeadFormState = {
  name: string;
  email: string;
  phone: string;
  message: string;
};

const teamProfiles = [
  {
    name: "Sarah Johnson",
    title: "Local Advisor",
    image: ADVISOR_WOMAN_SRC,
    description:
      "Sarah is often the first person clients meet. She helps turn goals, deadlines, and open questions into a clear year-round plan.",
  },
  {
    name: "David Case",
    title: "Tax Professional",
    image: ACCOUNTANT_SRC,
    description:
      "David focuses on tax planning and filing workflows for independent earners and pass-through owners, with practical next steps before tax season.",
  },
  {
    name: "Maria Bennett",
    title: "Bookkeeping Lead",
    image: BOOKKEEPER_SRC,
    description:
      "Maria keeps the day-to-day books moving: categorization, reconciliations, missing items, and the details that make planning conversations useful.",
  },
];

const iconMap: Record<ServicesIconKey, Icon> = {
  tax: IconFileInvoice,
  books: IconBook2,
  wealth: IconTrendingUp,
  planning: IconCalculator,
  owner: IconBuildingBank,
  organize: IconFileText,
  team: IconUsers,
  app: IconCreditCard,
};

const navItems = [
  {label: "Services", href: "#services"},
  {label: "How It Works", href: "#how-it-works"},
  {label: "Who We Help", href: "#who-we-help"},
  {label: "Reviews", href: "#reviews"},
  {label: "FAQ", href: "#faq"},
];

const heroTrustItems = [
  "Bookkeeping",
  "Tax Planning",
  "Tax Filing Support",
  "Wealth Management",
  "Local Fig Team",
  "Powered by our Fig App",
];

const problemCards: Array<{title: string; description: string; icon: Icon}> = [
  {title: "Books", description: "Day-to-day transactions and financial records", icon: IconBook2},
  {title: "Taxes", description: "Planning, estimates, and filing support", icon: IconCalculator},
  {title: "Wealth", description: "Investments, cash flow, and long-term goals", icon: IconChartBar},
];

const solutionCards: Array<{title: string; description: string; icon: Icon}> = [
  {
    title: "Books that stay current",
    description:
      "We help categorize transactions, reconcile accounts, organize records, and keep your financials tax-ready throughout the year.",
    icon: IconBook2,
  },
  {
    title: "Taxes without the annual surprise",
    description:
      "We help monitor income, deductions, estimated payments, entity structure, and year-end tax readiness before tax season becomes a fire drill.",
    icon: IconCalendar,
  },
  {
    title: "Wealth planning from real numbers",
    description:
      "Because Fig connects with your financial picture, wealth planning can be based on what is actually happening in your business.",
    icon: IconTrendingUp,
  },
];

const audiences: Array<{title: string; description: string; icon: Icon}> = [
  {
    title: "Freelancers & 1099 workers",
    description:
      "For independent workers who need clean books, quarterly tax visibility, deductions, and retirement planning without building their own finance department.",
    icon: IconUser,
  },
  {
    title: "Single-member LLCs & S-Corp owners",
    description:
      "For owners who need bookkeeping, tax planning, payroll coordination, owner comp and distribution visibility, and cleaner year-end reporting.",
    icon: IconHome,
  },
  {
    title: "Pass-through entities",
    description:
      "For partnerships, multi-member LLCs, and closely held entities that need organized books, tax-ready reporting, and better owner-level visibility.",
    icon: IconUsers,
  },
  {
    title: "Local service businesses",
    description:
      "For small firms, agencies, practices, and service operators who need monthly financial clarity and fewer surprises.",
    icon: IconBuilding,
  },
];

const appFeatures = [
  "Connected bank and credit card activity",
  "Transaction categorization and review",
  "Tax estimate visibility",
  "Quarterly tax planning support",
  "Document and receipt organization",
  "Bookkeeping status and open-item tracking",
  "Business and personal financial context",
  "Wealth planning coordination",
];

const trustBullets = [
  "Human review before filings or final recommendations",
  "Clear scope of services",
  "Secure client workflows and data protection",
  "Local advisor accountability and direct access",
  "Tax work performed or reviewed by qualified tax professionals",
  "Wealth management through regulated advisory channels, where applicable",
];

const reviewThemes = [
  {
    quote: "They finally brought clarity to my business finances. I always know what I owe and what to plan for.",
    name: "Emily R.",
    role: "Consultant",
  },
  {
    quote: "Fig's team is responsive, proactive, and truly invested in my success.",
    name: "Jason T.",
    role: "Agency Owner",
  },
  {
    quote: "From bookkeeping to planning, everything is in one place. Highly recommend.",
    name: "Dr. Priya K.",
    role: "Clinician",
  },
];

const faqs = [
  {
    question: "Do I need to use the Fig app?",
    answer:
      "Yes. The Fig app is how we keep your financial picture organized and visible throughout the year. Your local team uses it to support bookkeeping, tax planning, documents, and financial coordination.",
  },
  {
    question: "Is Fig replacing my accountant with AI?",
    answer:
      "No. Fig uses software to make the work more organized and proactive. Important bookkeeping, tax, and planning work is reviewed by qualified professionals.",
  },
  {
    question: "Do you only work with freelancers?",
    answer:
      "No. Fig is built for freelancers, 1099 workers, business owners, single-member LLCs, S-Corp owners, and pass-through entities.",
  },
  {
    question: "Can you file my taxes?",
    answer:
      "Tax preparation and e-filing are available through qualified tax professionals where offered by your local Fig team. Your exact scope will be confirmed before work begins.",
  },
  {
    question: "Is wealth management included?",
    answer:
      "Wealth management may be available through regulated advisory channels, depending on your local Fig team and service relationship.",
  },
  {
    question: "What makes Fig different from a normal accounting firm?",
    answer:
      "Most firms handle books, taxes, and wealth planning separately. Fig connects them through one software-supported process, so your local team can work from a clearer financial picture all year.",
  },
];

function ServiceIcon({card}: {card: ServicesCard}) {
  const Component = iconMap[card.icon];

  return (
    <span className={classes.cardIcon} aria-hidden="true">
      <Component size={24} />
    </span>
  );
}

const getWwwOrigin = () => {
  const hostname = process.env.WWW_HOSTNAME || "www.figwealth.io";
  const isLocalHostname = ["localhost", "127.0.0.1", "0.0.0.0"].includes(hostname);
  const useHttp = process.env.HTTPS_ENABLED !== "true" && isLocalHostname;
  const portValue = useHttp ? process.env.WWW_PORT_HTTP : process.env.WWW_PORT_HTTPS;
  const port = portValue ? `:${portValue}` : "";

  return `${useHttp ? "http" : "https"}://${hostname}${port}`;
};

const getProOrigin = () => {
  if (typeof window === "undefined") {
    return "";
  }

  const {protocol, hostname, port} = window.location;
  const baseHostname = hostname.replace(/^services\./i, "").replace(/^www\./i, "");
  const proHostname = process.env.PRO_HOSTNAME || `pro.${baseHostname}`;
  const isLocalProHost = ["localhost", "127.0.0.1", "0.0.0.0"].includes(proHostname);
  const proProtocol = isLocalProHost ? "http:" : protocol;
  const configuredPort = isLocalProHost ? process.env.PRO_PORT_HTTP : process.env.PRO_PORT_HTTPS;
  const proPort = configuredPort || (isLocalProHost ? port : "");

  return `${proProtocol}//${proHostname}${proPort ? `:${proPort}` : ""}`;
};

const getServiceSignupUrl = (office: ServicesOffice) => {
  const params = new URLSearchParams();

  if (office.firmRoutingSlug) {
    params.set("firm", office.firmRoutingSlug);
  }

  if (office.industrySlug) {
    params.set("industry", office.industrySlug);
  }

  const queryString = params.toString();

  return `${getProOrigin()}${SERVICE_SIGNUP_PATH}${queryString ? `?${queryString}` : ""}`;
};

const getContactHref = (office: ServicesOffice) => {
  if (office.phoneTel && office.phoneDisplay) {
    return office.phoneTel;
  }

  return office.showContactForm ? "#contact" : "/contact-us";
};

const getContactLabel = (office: ServicesOffice) => {
  if (office.phoneDisplay) {
    return `Call ${office.phoneDisplay}`;
  }

  return office.contactCtaLabel || "Contact Us";
};

function PrimaryButton({
  office,
  className,
  children,
  href = `#${PRICING_SECTION_ID}`,
}: {
  office: ServicesOffice;
  className?: string;
  children?: React.ReactNode;
  href?: string;
}) {
  const classesValue = `${classes.primaryButton} ${className || ""}`.trim();
  const label = children || office.signupCtaLabel || "Choose Your Support Level";

  return (
    <a href={href} className={classesValue}>
      {label}
      <IconArrowRight size={17} />
    </a>
  );
}

function PackageCta({office, plan}: {office: ServicesOffice; plan: ServicesPackage}) {
  const isContactPlan = plan.target === "contact";
  const planHref = isContactPlan ? getContactHref(office) : getServiceSignupUrl(office);
  const buttonClassName = isContactPlan
    ? `${classes.secondaryButton} ${classes.secondaryFilledButton} ${classes.packageButton}`
    : `${classes.primaryButton} ${classes.packageButton}`;

  return (
    <a href={planHref} className={buttonClassName}>
      {plan.cta}
      {isContactPlan && office.phoneDisplay ? <IconPhone size={17} /> : <IconArrowRight size={17} />}
    </a>
  );
}

function PackageCard({office, plan}: {office: ServicesOffice; plan: ServicesPackage}) {
  return (
    <article className={plan.featured ? classes.featuredPackage : ""} key={plan.name}>
      {plan.featured ? <span className={classes.packageBadge}>Most popular</span> : null}
      <h3>{plan.name}</h3>
      <p>{plan.description}</p>
      <strong>{plan.priceDisplay}</strong>
      <ul>
        {plan.includes.map((item) => (
          <li key={item}>
            <IconCheck size={16} />
            {item}
          </li>
        ))}
      </ul>
      <PackageCta office={office} plan={plan} />
    </article>
  );
}

function ContactButton({office, className}: {office: ServicesOffice; className?: string}) {
  const classesValue = `${classes.secondaryButton} ${className || ""}`.trim();
  const isPhone = Boolean(office.phoneDisplay && office.phoneTel);

  return (
    <a href={getContactHref(office)} className={classesValue}>
      {isPhone ? office.contactCtaLabel || "Call the Team" : getContactLabel(office)}
      {isPhone ? <IconPhone size={17} /> : <IconMail size={17} />}
    </a>
  );
}

function SecondaryHowItWorksLink() {
  return (
    <a href="#how-it-works" className={classes.secondaryButton}>
      See How Fig Works
      <IconArrowRight size={18} />
    </a>
  );
}

function ContactActions({office}: {office: ServicesOffice}) {
  const actions = [
    office.phoneTel && office.phoneDisplay
      ? {
          href: office.phoneTel,
          label: `Call ${office.phoneDisplay}`,
          meta: "Office phone",
          icon: IconPhone,
          primary: true,
        }
      : null,
    office.googleMapsUrl
      ? {
          href: office.googleMapsUrl,
          label: "Google Maps",
          meta: "Office profile",
          icon: IconMapPin,
          primary: false,
        }
      : null,
    office.yelpUrl
      ? {
          href: office.yelpUrl,
          label: "Yelp",
          meta: "Reviews and listing",
          icon: IconStarFilled,
          primary: false,
        }
      : null,
    !office.phoneTel && office.showContactForm
      ? {
          href: "#contact",
          label: office.contactCtaLabel || "Contact Us",
          meta: "Send a message",
          icon: IconMail,
          primary: true,
        }
      : null,
  ].filter(Boolean) as Array<{
    href: string;
    label: string;
    meta: string;
    icon: Icon;
    primary: boolean;
  }>;

  if (!actions.length) {
    return null;
  }

  return (
    <div className={classes.contactActions}>
      {actions.map((action) => {
        const Component = action.icon;

        return (
          <a
            href={action.href}
            key={action.label}
            className={`${classes.contactAction} ${action.primary ? classes.contactActionPrimary : ""}`}
            target={action.href.startsWith("http") ? "_blank" : undefined}
            rel={action.href.startsWith("http") ? "noreferrer" : undefined}>
            <Component size={18} />
            <span>
              <strong>{action.label}</strong>
              <small>{action.meta}</small>
            </span>
          </a>
        );
      })}
    </div>
  );
}

function ServicesLeadForm({office}: {office: ServicesOffice}) {
  const [form, setForm] = useState<ServicesLeadFormState>({name: "", email: "", phone: "", message: ""});
  const [submissionState, setSubmissionState] = useState<SubmissionState>("idle");

  const updateField =
    (field: keyof ServicesLeadFormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((current) => ({...current, [field]: event.target.value}));
    };

  const submitLead = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmissionState("submitting");

    try {
      const response = await fetch("/api/web/landing-lead", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          ...form,
          audience: "services",
          intent: office.contactFormIntent || "services-contact",
          source: typeof window === "undefined" ? office.slug : window.location.pathname,
          host: typeof window === "undefined" ? "" : window.location.host,
        }),
      });

      if (!response.ok) {
        throw new Error("Services lead request failed");
      }

      setSubmissionState("success");
      setForm({name: "", email: "", phone: "", message: ""});
    } catch {
      setSubmissionState("error");
    }
  };

  return (
    <form className={classes.servicesLeadForm} onSubmit={submitLead}>
      <label>
        Name
        <input value={form.name} onChange={updateField("name")} placeholder="Your name" required />
      </label>
      <label>
        Email
        <input value={form.email} onChange={updateField("email")} type="email" placeholder="you@example.com" required />
      </label>
      <label>
        Phone
        <input value={form.phone} onChange={updateField("phone")} type="tel" placeholder="Optional" />
      </label>
      <label className={classes.fullField}>
        Message
        <textarea value={form.message} onChange={updateField("message")} placeholder="What should we know?" rows={5} />
      </label>
      <button type="submit" className={classes.primaryButton} disabled={submissionState === "submitting"}>
        <IconMail size={18} />
        {submissionState === "submitting" ? "Sending..." : "Send Message"}
      </button>
      {submissionState === "success" ? (
        <p className={classes.successMessage}>
          <IconCheck size={18} />
          Thanks. We received your message.
        </p>
      ) : null}
      {submissionState === "error" ? (
        <p className={classes.errorMessage}>Something went wrong. Please try again.</p>
      ) : null}
    </form>
  );
}

function ServicesNav({office, pricingHref = `#${PRICING_SECTION_ID}`}: {office: ServicesOffice; pricingHref?: string}) {
  return (
    <nav className={classes.nav}>
      <a href="/" aria-label={`${SERVICES_BRAND_NAME} home`} className={classes.brand}>
        <img src={LOGO_SRC} alt="Fig" />
      </a>
      <div className={classes.navLinks}>
        {navItems.map((item) => (
          <a href={item.href.startsWith("#") ? `/${item.href}` : item.href} key={item.label}>
            {item.label}
          </a>
        ))}
      </div>
      <PrimaryButton office={office} className={classes.navButton} href={pricingHref} />
    </nav>
  );
}

function HeroVisual({office}: {office: ServicesOffice}) {
  const heroImageSrc = office.heroImageSrc || HERO_IMAGE_SRC;
  const heroImageAlt = office.heroImageAlt || "Local Fig team reviewing a client financial picture";
  const hasCustomHeroImage = Boolean(office.heroImageSrc);
  const heroVisualClassName = `${classes.heroVisual} ${hasCustomHeroImage ? classes.heroVisualCustom : ""}`.trim();
  const currentYear = new Date().getFullYear();

  return (
    <div className={heroVisualClassName}>
      {hasCustomHeroImage ? (
        <img className={classes.heroStandaloneImage} src={heroImageSrc} alt={heroImageAlt} />
      ) : (
        <div className={classes.heroTeamImage}>
          <img src={heroImageSrc} alt={heroImageAlt} />
        </div>
      )}
      <div className={classes.heroAppCard} aria-label="Fig app dashboard preview">
        <div className={classes.appChrome}>
          <img src={LOGO_SRC} alt="Fig" />
          <IconLock size={19} />
          <IconUser size={20} />
        </div>
        <nav className={classes.heroAppTabs} aria-label="Fig app sections">
          <span>Overview</span>
          <span>Docs</span>
          <span>Messages</span>
          <span>Help</span>
        </nav>
        <p className={classes.heroAppLabel}>Financial overview</p>
        <div className={classes.appMetricGrid}>
          <div>
            <span>Income</span>
            <strong>$245,000</strong>
            <small>Up 8.2% vs last year</small>
          </div>
          <div>
            <span>Expenses</span>
            <strong>$94,500</strong>
            <small>Up 4.1% vs last year</small>
          </div>
          <div>
            <span>Tax Estimate</span>
            <strong>$37,800</strong>
            <small>Up 6.7% vs last year</small>
          </div>
          <div>
            <span>Effective Rate</span>
            <strong>15.4%</strong>
            <small>Down 0.3 pp vs last year</small>
          </div>
        </div>
        <p className={classes.heroAppLabel}>Tasks</p>
        <ul>
          <li>
            <IconCircleCheck size={19} />
            <span>
              Review Q2 Estimated Taxes
              <small>Due Jun 15</small>
            </span>
          </li>
          <li>
            <IconFileText size={19} />
            <span>
              Upload Q2 Expenses
              <small>Due Jun 20</small>
            </span>
          </li>
          <li>
            <IconFileInvoice size={19} />
            <span>
              Sign {currentYear} Tax Extension
              <small>Due Jun 30</small>
            </span>
          </li>
        </ul>
        <a href="#how-it-works">
          View all tasks
          <IconArrowRight size={16} />
        </a>
      </div>
    </div>
  );
}

function ProblemVisual() {
  return (
    <div className={classes.problemVisual} aria-label="Books, taxes, and wealth flowing into one Fig financial picture">
      <div className={classes.problemCards}>
        {problemCards.map((card) => {
          const Component = card.icon;

          return (
            <article key={card.title}>
              <Component size={26} />
              <h3>{card.title}</h3>
              <p>{card.description}</p>
            </article>
          );
        })}
      </div>
      <svg className={classes.problemLines} viewBox="0 0 720 210" aria-hidden="true">
        <path d="M110 28 C145 96 270 130 360 168" />
        <path d="M360 28 C360 92 360 122 360 168" />
        <path d="M610 28 C575 96 450 130 360 168" />
      </svg>
      <div className={classes.unifiedCard}>
        <strong>One Fig Financial Picture</strong>
        <span>Accurate. Connected. Actionable.</span>
      </div>
    </div>
  );
}

function UnknownOffice() {
  return (
    <div className={classes.page}>
      <ServicesNav
        office={getServicesOfficeBySlug(SERVICES_DEFAULT_OFFICE_SLUG) as ServicesOffice}
        pricingHref={`/#${PRICING_SECTION_ID}`}
      />
      <main className={classes.notFound}>
        <p className={classes.eyebrow}>Page not found</p>
        <h1>This Fig services page is not available.</h1>
        <Link to={LOCATIONS_PATH} className={classes.inlineLink}>
          Choose a location
          <IconArrowRight size={18} />
        </Link>
      </main>
    </div>
  );
}

function LocationsDirectory() {
  const office = getServicesOfficeBySlug(SERVICES_DEFAULT_OFFICE_SLUG) as ServicesOffice;
  const wwwOrigin = getWwwOrigin();

  return (
    <div className={classes.page}>
      <ServicesNav office={office} pricingHref={`/#${PRICING_SECTION_ID}`} />

      <main>
        <section className={classes.locationsHero}>
          <p className={classes.eyebrow}>Choose location</p>
          <h1>Find the local Fig team that fits where you work.</h1>
          <p>
            Choose a local office to see service-area details, call the team, and get connected books, taxes, and wealth
            planning support.
          </p>
        </section>

        <section className={classes.locationsGrid} aria-label="Fig locations">
          {servicesLocationOffices.map((location) => (
            <article className={classes.locationCard} key={location.slug}>
              <div>
                <span>
                  <IconMapPin size={18} />
                  {location.marketLabel}
                </span>
                <h2>{location.name}</h2>
                <p>{location.summary}</p>
              </div>
              <ul>
                <li>
                  <IconCheck size={17} />
                  {location.serviceArea}
                </li>
                <li>
                  <IconCalendar size={17} />
                  {location.hours.join(" / ")}
                </li>
                <li>
                  <IconPhone size={17} />
                  {location.phoneDisplay || SERVICES_PHONE_DISPLAY}
                </li>
              </ul>
              <div className={classes.locationActions}>
                <Link to={`/locations/${location.slug}`} className={classes.primaryButton}>
                  View location
                </Link>
                <a href={location.phoneTel || SERVICES_PHONE_TEL} className={classes.secondaryButton}>
                  Call office
                  <IconPhone size={18} />
                </a>
              </div>
            </article>
          ))}
        </section>
      </main>

      <footer className={classes.footer}>
        <span>Copyright {new Date().getFullYear()} Fig Wealth Advisors, LLC.</span>
        <nav>
          <a href={INDUSTRIES_PATH}>Industries</a>
          <a href={`${wwwOrigin}/privacy-policy`}>Privacy Policy</a>
          <a href={`${wwwOrigin}/terms-of-service`}>Terms of Service</a>
        </nav>
      </footer>
    </div>
  );
}

function IndustriesDirectory() {
  const office = getServicesOfficeBySlug(SERVICES_DEFAULT_OFFICE_SLUG) as ServicesOffice;
  const wwwOrigin = getWwwOrigin();

  return (
    <div className={classes.page}>
      <ServicesNav office={office} pricingHref={`/#${PRICING_SECTION_ID}`} />

      <main>
        <section className={classes.locationsHero}>
          <p className={classes.eyebrow}>Choose industry</p>
          <h1>Service funnels for industry-specific accounting needs.</h1>
          <p>
            Choose a virtual services page to start with the right industry context before Fig Pro routes the lead to an
            enabled firm.
          </p>
        </section>

        <section className={classes.locationsGrid} aria-label="Fig industry services">
          {servicesIndustryPages.map((industry) => (
            <article className={classes.locationCard} key={industry.slug}>
              <div>
                <span>
                  <IconBuilding size={18} />
                  {industry.marketLabel}
                </span>
                <h2>{industry.name}</h2>
                <p>{industry.summary}</p>
              </div>
              <ul>
                <li>
                  <IconCheck size={17} />
                  {industry.serviceArea}
                </li>
                <li>
                  <IconCalendar size={17} />
                  {industry.hours.join(" / ")}
                </li>
                <li>
                  <IconMail size={17} />
                  Contact form available
                </li>
              </ul>
              <div className={classes.locationActions}>
                <Link to={`${INDUSTRIES_PATH}/${industry.slug}`} className={classes.primaryButton}>
                  View industry
                </Link>
                <Link
                  to={`${INDUSTRIES_PATH}/${industry.slug}#${PRICING_SECTION_ID}`}
                  className={classes.secondaryButton}>
                  Start signup
                  <IconArrowRight size={18} />
                </Link>
              </div>
            </article>
          ))}
        </section>
      </main>

      <footer className={classes.footer}>
        <span>Copyright {new Date().getFullYear()} Fig Wealth Advisors, LLC.</span>
        <nav>
          <a href={LOCATIONS_PATH}>Locations</a>
          <a href={`${wwwOrigin}/privacy-policy`}>Privacy Policy</a>
          <a href={`${wwwOrigin}/terms-of-service`}>Terms of Service</a>
        </nav>
      </footer>
    </div>
  );
}

function LocalPage({office}: {office: ServicesOffice}) {
  const wwwOrigin = getWwwOrigin();
  const isIndustryPage = office.templateType === "industry";
  const canonicalOfficePath = isIndustryPage
    ? `${INDUSTRIES_PATH}/${office.slug}`
    : office.slug === SERVICES_DEFAULT_OFFICE_SLUG
    ? LOCATIONS_PATH
    : `/locations/${office.slug}`;
  const canonicalPageLabel = isIndustryPage ? "Industry Page" : "Location Page";
  const localSectionParagraphs = office.localSectionParagraphs || [
    "Whether you are a consultant, contractor, creative, clinician, real estate professional, agency owner, or pass-through business owner, your financial life probably does not fit neatly into a payroll stub.",
    `Fig is built for ${office.localDescriptor} who need more than once-a-year tax help. You get local accountability with the software infrastructure of a modern financial platform.`,
  ];
  const selfServicePackage = office.packages.find((plan) => plan.name === "Self Service");
  const mainPackages = office.packages.filter((plan) => plan.name !== "Self Service");
  const pageTrustItems = isIndustryPage
    ? ["Bookkeeping", "Tax Planning", "Tax Filing Support", "Retirement Coordination", "Industry-Aware Routing"]
    : heroTrustItems;
  const pageReviews = office.reviews || reviewThemes;
  const heroMarketLabel = office.marketLabel.trim();
  const showHeroEyebrow = heroMarketLabel.replace(/\.$/, "").toLowerCase() !== "fig";

  return (
    <div className={classes.page}>
      <ServicesNav office={office} />

      <header className={classes.hero}>
        <div className={classes.heroCopy}>
          {showHeroEyebrow && (
            <p className={classes.eyebrow}>
              {isIndustryPage ? (
                <IconBuilding size={15} />
              ) : office.templateType === "location" ? (
                <IconMapPin size={15} />
              ) : null}
              {heroMarketLabel}
            </p>
          )}
          <h1>{office.heroTitle}</h1>
          <div className={classes.heroBody}>
            <p>{office.heroDescription}</p>
            <p>{office.heroSupportText}</p>
          </div>
          <div className={classes.heroActions}>
            <PrimaryButton office={office} />
            <ContactButton office={office} className={classes.secondaryFilledButton} />
            <SecondaryHowItWorksLink />
          </div>
        </div>
        <HeroVisual office={office} />
      </header>

      <main>
        <section className={classes.heroTrustStrip} aria-label="Fig services">
          {pageTrustItems.map((item) => (
            <span key={item}>
              <IconCheck size={14} />
              {item}
            </span>
          ))}
        </section>

        <section className={classes.problemSection}>
          <div className={classes.sectionCopy}>
            <p className={classes.eyebrow}>Most financial help is split apart.</p>
            <h2>Your bookkeeper, tax preparer, and wealth advisor should not all be working from different numbers.</h2>
            <p>
              Most independent earners and business owners live in the messy middle: business income, personal income,
              tax estimates, retirement planning, deductions, owner draws, distributions, and cash flow all overlap.
            </p>
            <p>
              Traditional firms usually handle one piece at a time. Fig connects the work that usually gets separated.
            </p>
          </div>
          <ProblemVisual />
        </section>

        <section className={classes.solutionSection}>
          <div className={classes.sectionCopy}>
            <p className={classes.eyebrow}>What Fig does differently</p>
            <h2>
              {isIndustryPage
                ? "One service team. One app. One financial picture."
                : "One local team. One app. One financial picture."}
            </h2>
            <p>
              Fig combines financial support with software built for people whose business and personal finances are
              closely connected. The app keeps the work visible. The service team keeps it human.
            </p>
          </div>
          <div className={classes.solutionCards}>
            {solutionCards.map((card) => {
              const Component = card.icon;

              return (
                <article key={card.title}>
                  <Component size={24} />
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className={classes.localTrustSection} id="about">
          <div>
            <p className={classes.eyebrow}>{office.localSectionEyebrow || `Built for ${office.region} independents`}</p>
            <h2>{office.localSectionTitle || "A local Fig team that understands how people here actually earn."}</h2>
            {localSectionParagraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            <div className={classes.servedAreas}>
              {office.servedAreas.map((area) => (
                <span key={area}>{area}</span>
              ))}
            </div>
          </div>
          {office.showTeamProfiles ? (
            <aside className={classes.advisorCard}>
              <div className={classes.advisorCardHeader}>
                <h3>Meet your {office.shortName} Fig team</h3>
                <p>People you can talk to, backed by software that keeps the work organized between conversations.</p>
              </div>
              <div className={classes.advisorProfiles}>
                {teamProfiles.map((profile) => (
                  <article key={profile.name}>
                    <img src={profile.image} alt={`${profile.name}, ${profile.title}`} />
                    <div>
                      <h4>{profile.name}</h4>
                      <span>{profile.title}</span>
                      <p>{profile.description}</p>
                    </div>
                  </article>
                ))}
              </div>
            </aside>
          ) : (
            <aside className={classes.advisorCard}>
              <div className={classes.advisorCardHeader}>
                <h3>{office.detailCardTitle || `Designed for ${office.shortName}`}</h3>
                <p>
                  {office.detailCardDescription ||
                    "Fig uses the signup flow to collect enough context for the service team to scope the relationship."}
                </p>
              </div>
              {office.detailHighlights?.length ? (
                <ul className={classes.detailHighlights}>
                  {office.detailHighlights.map((highlight) => (
                    <li key={highlight}>
                      <IconCheck size={17} />
                      {highlight}
                    </li>
                  ))}
                </ul>
              ) : null}
              <ContactActions office={office} />
            </aside>
          )}
        </section>

        <section className={classes.audienceSection} id="who-we-help">
          <div className={classes.centerHeader}>
            <p className={classes.eyebrow}>Who We Help</p>
            <h2>Built for people whose income, taxes, and planning need more context.</h2>
          </div>
          <div className={classes.audienceGrid}>
            {audiences.map((audience) => {
              const Component = audience.icon;

              return (
                <article key={audience.title}>
                  <Component size={34} />
                  <h3>{audience.title}</h3>
                  <p>{audience.description}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className={classes.appSection}>
          <div className={classes.appVisual}>
            <HeroPhoneAnimation showFloatingElements variant="light" />
          </div>
          <div className={classes.appCopy}>
            <p className={classes.eyebrow}>Powered by software, delivered by people</p>
            <h2>The Fig app keeps your financial picture visible between meetings.</h2>
            <p>
              Most accounting relationships go quiet until something is missing, late, or confusing. Fig gives you a
              clearer way to stay organized throughout the year.
            </p>
            <ul>
              {appFeatures.map((feature) => (
                <li key={feature}>
                  <IconCircleCheck size={17} />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className={classes.humanReviewSection}>
          <div>
            <p className={classes.eyebrow}>Technology where it helps. Human judgment where it matters.</p>
            <h2>No important financial decision should be left to a black box.</h2>
            <p>
              Fig uses software to organize the work, surface issues, and keep your financial picture current. But
              bookkeeping decisions, tax positions, filings, and planning recommendations are reviewed by qualified
              professionals before they are delivered.
            </p>
          </div>
          <ul>
            {trustBullets.map((bullet) => (
              <li key={bullet}>
                <IconShieldCheck size={18} />
                {bullet}
              </li>
            ))}
          </ul>
        </section>

        <section className={classes.servicesSection} id="services">
          <div className={classes.centerHeader}>
            <p className={classes.eyebrow}>Our services</p>
            <h2>
              {isIndustryPage ? "What your Fig service team can help with" : "What your local Fig team can help with"}
            </h2>
          </div>
          <div className={classes.serviceGrid}>
            {office.services.map((card) => (
              <article key={card.title}>
                <ServiceIcon card={card} />
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className={classes.processSection} id="how-it-works">
          <div className={classes.centerHeader}>
            <p className={classes.eyebrow}>How it works</p>
            <h2>A simpler year-round process</h2>
          </div>
          <div className={classes.processGrid}>
            {office.process.map((step, index) => (
              <article key={step.title}>
                <span>{index + 1}</span>
                <ServiceIcon card={step} />
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className={classes.reviewsSection} id="reviews">
          <div className={classes.sectionCopy}>
            <h2>Trusted by people who needed more than another tax-season scramble.</h2>
          </div>
          <div className={classes.reviewGrid}>
            {pageReviews.map((review) => (
              <article key={review.name}>
                <span>
                  {Array.from({length: 5}).map((_, index) => (
                    <IconStarFilled size={15} key={index} />
                  ))}
                </span>
                <p>"{review.quote}"</p>
                <strong>
                  {review.name}, {review.role}
                </strong>
              </article>
            ))}
            {isIndustryPage ? (
              <article className={classes.googleReviewCard}>
                <strong>Industry fit</strong>
                <p>
                  Fig Pro stores the industry context before routing the lead to an enabled firm that can support the
                  service relationship.
                </p>
                <a href={`#${PRICING_SECTION_ID}`}>Start signup</a>
              </article>
            ) : (
              <article className={classes.googleReviewCard}>
                <img src={GOOGLE_IMAGE_SRC} alt="Google" />
                <strong>5.0</strong>
                <span>
                  {Array.from({length: 5}).map((_, index) => (
                    <IconStarFilled size={15} key={index} />
                  ))}
                </span>
                <a href={office.googleMapsUrl || "#office"}>Read all reviews</a>
              </article>
            )}
          </div>
        </section>

        <section className={classes.pricingSection} id={PRICING_SECTION_ID}>
          <div className={classes.sectionCopy}>
            <h2>Choose the level of support that fits your financial life.</h2>
          </div>
          <div className={classes.pricingStack}>
            {selfServicePackage ? (
              <article className={classes.selfServicePackage}>
                <div>
                  <span>App-only option</span>
                  <h3>{selfServicePackage.name}</h3>
                  <p>{selfServicePackage.description}</p>
                </div>
                <strong>{selfServicePackage.priceDisplay}</strong>
                <PackageCta office={office} plan={selfServicePackage} />
              </article>
            ) : null}
            <div className={classes.packageGrid}>
              {mainPackages.map((plan) => (
                <PackageCard office={office} plan={plan} key={plan.name} />
              ))}
            </div>
          </div>
        </section>

        <section className={classes.faqSection} id="faq">
          <h2>Frequently asked questions</h2>
          <div>
            {faqs.map((faq) => (
              <details key={faq.question}>
                <summary>
                  {faq.question}
                  <IconPlus size={17} />
                </summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className={classes.finalCta}>
          <div>
            <h2>Ready for financial help that actually fits how you earn?</h2>
            <p>
              {isIndustryPage
                ? "Start with the right industry context and see how connected bookkeeping, tax planning, and wealth management can work together throughout the year."
                : "Meet your local Fig team and see how connected bookkeeping, tax planning, and wealth management can work together throughout the year."}
            </p>
          </div>
          <div>
            <PrimaryButton office={office} />
            <ContactButton office={office} className={classes.secondaryFilledButton} />
          </div>
        </section>

        {office.showContactForm ? (
          <section className={classes.contactFormSection} id="contact">
            <div className={classes.sectionCopy}>
              <p className={classes.eyebrow}>Contact</p>
              <h2>{office.contactFormTitle || "Contact the Fig services team"}</h2>
              <p>
                {office.contactFormDescription ||
                  "Send a note and the Fig team will route your question to the right person."}
              </p>
            </div>
            <ServicesLeadForm office={office} />
          </section>
        ) : null}

        <section className={classes.officeSection} id="office">
          <div className={classes.footerBrand}>
            <img src={LOGO_SRC} alt="Fig" />
            <p>The service firm for people whose money does not fit in a W-2 box.</p>
            <span>{office.serviceArea}</span>
          </div>
          <div>
            <h2>Contact</h2>
            {office.phoneDisplay ? (
              <p>
                <IconPhone size={18} />
                <a href={office.phoneTel}>{office.phoneDisplay}</a>
              </p>
            ) : null}
            <p>
              <IconMail size={18} />
              <a href={office.showContactForm ? "#contact" : `${wwwOrigin}/contact-us`}>Contact Fig</a>
            </p>
            <ContactActions office={office} />
          </div>
          <div>
            <h2>{isIndustryPage ? "Availability" : "Office Hours"}</h2>
            {office.hours.map((hour) => (
              <p key={hour}>{hour}</p>
            ))}
            <p>Virtual consultations available</p>
          </div>
          <div>
            <h2>Quick Links</h2>
            <a href="#services">Services</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#who-we-help">Who We Help</a>
            <a href="#faq">FAQ</a>
            <a href={LOCATIONS_PATH}>Locations</a>
            <a href={INDUSTRIES_PATH}>Industries</a>
            <a href={canonicalOfficePath}>{canonicalPageLabel}</a>
          </div>
        </section>
      </main>

      <footer className={classes.footer}>
        <span>Copyright {new Date().getFullYear()} Fig Wealth Advisors, LLC.</span>
        <p>
          Investment advisory services are provided through regulated advisory channels, where applicable. Tax
          preparation and e-filing services are provided where available through qualified tax professionals. Fig does
          not provide audit, review, compilation, or other attest services unless expressly agreed in writing by a
          properly licensed provider.
        </p>
        <nav>
          <a href={LOCATIONS_PATH}>Locations</a>
          <a href={INDUSTRIES_PATH}>Industries</a>
          <a href={`${wwwOrigin}/privacy-policy`}>Privacy Policy</a>
          <a href={`${wwwOrigin}/terms-of-service`}>Terms of Service</a>
        </nav>
      </footer>
    </div>
  );
}

export default function Services() {
  const {officeSlug, locationSlug, industrySlug} = useParams();

  if (industrySlug) {
    const industry = getServicesIndustryBySlug(industrySlug);

    if (!industry) {
      return <UnknownOffice />;
    }

    return <LocalPage office={industry} />;
  }

  const selectedOfficeSlug = locationSlug || officeSlug || SERVICES_DEFAULT_OFFICE_SLUG;
  const office = getServicesOfficeBySlug(selectedOfficeSlug);

  if (!office) {
    return <UnknownOffice />;
  }

  return <LocalPage office={office} />;
}

export {IndustriesDirectory, LocationsDirectory};
